import { inject, Injectable } from '@angular/core';
import { assertNever } from 'assert-never';
import { Observable, Subject, take } from 'rxjs';

import { GroupStoreService } from '@shure/cloud/device-management/shared/services';
import { Space, Group, InventoryDevice } from '@shure/cloud/shared/models/devices';

import { GroupsApiService } from '../api/groups-api.service';
import { InventoryDevicesApiService } from '../api/inventory-devices-api.service';
import { SpacesApiService } from '../api/spaces-api.service';

export interface InventoryDataAddedEvent<TData> {
	type: 'added';
	data: TData;
}

export interface InventoryDataUpdatedEvent<TData> {
	type: 'updated';
	data: TData;
}

export interface InventoryDataRemovedEvent {
	type: 'removed';
	id: string;
}

export type InventoryDataEvent<TData> =
	InventoryDataAddedEvent<TData> | InventoryDataUpdatedEvent<TData> | InventoryDataRemovedEvent;

/**
 * Service responsible for orchestrating the loading and management of inventory data, including devices, groups, and cloud spaces.
 * Provides a higher-level of abstraction for broadcasting events to the view layer and ensuring data consistency.
 * TODO (bisem): Move DeviceRelationshipService management from the inventory API service to this service
 */
@Injectable({ providedIn: 'root' })
export class InventoryDataAggregationService {
	private readonly groupsApiService = inject(GroupsApiService);
	private readonly spacesApiService = inject(SpacesApiService);
	private readonly inventoryDevicesApiService = inject(InventoryDevicesApiService);
	private readonly groupStoreService = inject(GroupStoreService);

	private readonly _deviceEvents = new Subject<InventoryDataEvent<InventoryDevice>>();
	public readonly deviceEvents$ = this._deviceEvents.asObservable();

	private readonly _groupEvents = new Subject<InventoryDataEvent<Group>>();
	public readonly groupEvents$ = this._groupEvents.asObservable();

	private readonly _spaceEvents = new Subject<InventoryDataEvent<Space>>();
	public readonly spaceEvents$ = this._spaceEvents.asObservable();

	constructor() {
		this.subscribeToDeviceEvents();
		this.subscribeToGroupEvents();
		this.subscribeToSpaceEvents();
	}

	public getCachedDevices$(): Observable<InventoryDevice[]> {
		return this.inventoryDevicesApiService.getInventoryDevices$().pipe(take(1));
	}

	public getCachedGroups$(): Observable<Group[]> {
		return this.groupsApiService.getCachedGroups$();
	}

	public getCachedSpaces$(): Observable<Space[]> {
		return this.spacesApiService.getCachedSpaces$();
	}

	private subscribeToDeviceEvents(): void {
		this.inventoryDevicesApiService.getInventoryDeviceEvents$().subscribe((event) => {
			switch (event.type) {
				case 'added':
					this.updateDeviceGroupAndSpace(event.device);
					this._deviceEvents.next({ type: 'added', data: event.device });
					break;
				case 'updated':
					this.updateDeviceGroupAndSpace(event.device);
					this._deviceEvents.next({ type: 'updated', data: event.device });
					break;
				case 'removed':
					this.groupStoreService.removeDevice(event.device.id);
					this._deviceEvents.next({ type: 'removed', id: event.device.id });
					break;
				default:
					assertNever(event.type);
			}
		});
	}

	private updateDeviceGroupAndSpace(device: InventoryDevice): void {
		const groupId = device.features.group?.group?.id;
		if (groupId) {
			this.groupStoreService.setDeviceGroup(device.id, groupId);
		} else {
			this.groupStoreService.removeDeviceFromGroup(device.id);
		}

		const spaceId = device.features.space?.space?.id;
		if (spaceId) {
			this.groupStoreService.setDeviceSpace(device.id, spaceId);
		} else {
			this.groupStoreService.removeDeviceFromSpace(device.id);
		}
	}

	private subscribeToGroupEvents(): void {
		this.groupsApiService.groupAdded$.subscribe((group) => {
			this.groupStoreService.addGroup(group);
			this._groupEvents.next({ type: 'added', data: group });
		});

		this.groupsApiService.groupRemoved$.subscribe((groupId) => {
			this.groupStoreService.removeGroup(groupId);
			this._groupEvents.next({ type: 'removed', id: groupId });
		});

		this.groupsApiService.groupSnapshot$.subscribe((updatedGroup) => {
			let existingGroup;

			try {
				existingGroup = this.groupStoreService.getGroup(updatedGroup.id);
			} catch {
				this.groupStoreService.addGroup(updatedGroup);
				this._groupEvents.next({ type: 'added', data: updatedGroup });
				return;
			}

			if (existingGroup.name !== updatedGroup.name) {
				this.groupStoreService.renameGroup(updatedGroup.id, updatedGroup.name);
				this._groupEvents.next({ type: 'updated', data: updatedGroup });
			}

			if (existingGroup.parentId !== updatedGroup.parentId) {
				this.groupStoreService.moveGroup(updatedGroup.id, updatedGroup.parentId);
				this._groupEvents.next({ type: 'updated', data: updatedGroup });
			}
		});

		this.groupsApiService.suspended$.subscribe(() => {
			this.groupStoreService.clear();
		});
	}

	private subscribeToSpaceEvents(): void {
		this.spacesApiService.spaceAdded$.subscribe((cloudSpace) => {
			this.groupStoreService.addOrUpdateSpace(cloudSpace);
			this._spaceEvents.next({ type: 'added', data: cloudSpace });
		});

		this.spacesApiService.spaceRemoved$.subscribe((cloudSpaceId) => {
			this.groupStoreService.removeSpace(cloudSpaceId);
			this._spaceEvents.next({ type: 'removed', id: cloudSpaceId });
		});

		this.spacesApiService.spaceSnapshot$.subscribe((cloudSpace) => {
			this.groupStoreService.addOrUpdateSpace(cloudSpace);
			this._spaceEvents.next({ type: 'updated', data: cloudSpace });
		});
	}
}
